import { useWatch } from "react-hook-form";
import { IDefaultForm } from "types/global";
import { useEffect, useState } from "react";
import { IMaintenanceItem } from "types/Logistic/maintenance";
import { MaintenanceDiscountType, PriceVatType } from "generated/sales";

export const useMaintenanceSummary = (
  control: IDefaultForm["control"],
  setValue: IDefaultForm["setValue"],
  name: "ma_item_list"
) => {
  const itemList: IMaintenanceItem[] = useWatch({
    control,
    name,
  });

  const additionalDiscount: number = useWatch({
    control,
    name: "additional_discount",
  });

  const additionalDiscountType: string = useWatch({
    control,
    name: "additional_discount_type",
  });

  const priceVatType: string = useWatch({
    control,
    name: "price_vat_type",
  });

  const [summary, setSummary] = useState({
    additional_discount: 0,
    post_discount_amount: 0,
    pre_discount_amount: 0,
    pre_vat_amount: 0,
    vat_exempted_amount: 0,
    vat_0_percent_amount: 0,
    vat_7_percent_amount: 0,
    vat_amount: 0,
    net_amount: 0,
    withholding_tax_amount: 0,
    total_amount: 0,
  });

  useEffect(() => {
    setSummary(
      ({
        additional_discount,
        net_amount,
        pre_vat_amount,
        pre_discount_amount,
        post_discount_amount,
        total_amount,
        vat_0_percent_amount,
        vat_7_percent_amount,
        vat_amount,
        vat_exempted_amount,
        withholding_tax_amount,
        // shipping_cost,
      }) => {
        const total_pre_discount_amount = itemList.reduce(
          (acc, item) => acc + (item.pre_discount_amount || 0),
          0
        );

        pre_discount_amount = total_pre_discount_amount;

        const total_pre_vat_amount = itemList.reduce(
          (acc, item) => acc + (item.post_discount_amount || 0),
          0
        );

        pre_vat_amount = total_pre_vat_amount;
        post_discount_amount = total_pre_vat_amount;

        if (additionalDiscountType === MaintenanceDiscountType.Baht) {
          additional_discount = additionalDiscount || 0;
        } else {
          additional_discount =
            total_pre_vat_amount * ((additionalDiscount || 0) / 100);
        }

        post_discount_amount = total_pre_vat_amount - additional_discount;

        vat_exempted_amount = itemList
          .filter((item) => item?.vat_percentage === "ไม่มี")
          .reduce((acc, item) => {
            const item_additional_discount =
              additional_discount *
                ((item.post_discount_amount || 0) / total_pre_vat_amount) || 0;
            return (
              acc +
              ((item.post_discount_amount || 0) - item_additional_discount)
            );
          }, 0);

        vat_0_percent_amount = itemList
          .filter((item) => item?.vat_percentage === "0 %")
          .reduce((acc, item) => {
            const item_additional_discount =
              additional_discount *
                ((item.post_discount_amount || 0) / total_pre_vat_amount) || 0;
            return (
              acc +
              ((item.post_discount_amount || 0) - item_additional_discount)
            );
          }, 0);

        if (priceVatType === PriceVatType.ExcludedVat) {
          vat_7_percent_amount = itemList
            .filter((item) => item?.vat_percentage === "7 %")
            .reduce((acc, item) => {
              const item_additional_discount =
                additional_discount *
                  ((item.post_discount_amount || 0) / total_pre_vat_amount) ||
                0;
              return (
                acc +
                ((item.post_discount_amount || 0) - item_additional_discount)
              );
            }, 0);
          vat_amount = vat_7_percent_amount * 0.07;

          withholding_tax_amount =
            itemList
              .filter(
                (item) =>
                  !["ยังไม่ระบุ", "ไม่มี", "", undefined, null].includes(
                    item?.withholding_tax_type
                  )
              )
              .reduce((acc, item) => {
                const item_additional_discount =
                  additional_discount *
                    ((item.post_discount_amount || 0) / total_pre_vat_amount) ||
                  0;
                return (
                  acc +
                  (((item.post_discount_amount || 0) -
                    item_additional_discount) *
                    parseFloat(item?.withholding_tax_type || "0")) /
                    100
                );
              }, 0) || 0;
        } else {
          vat_7_percent_amount = itemList
            .filter((item) => item?.vat_percentage === "7 %")
            .reduce((acc, item) => {
              const item_additional_discount =
                additional_discount *
                  ((item.post_discount_amount || 0) / total_pre_vat_amount) ||
                0;
              return (
                acc +
                ((item.post_discount_amount || 0) - item_additional_discount) /
                  1.07
              );
            }, 0);
          vat_amount = vat_7_percent_amount * 0.07;

          withholding_tax_amount =
            itemList
              .filter(
                (item) =>
                  !["ยังไม่ระบุ", "ไม่มี", "", undefined, null].includes(
                    item?.withholding_tax_type
                  )
              )
              .reduce((acc, item) => {
                const item_additional_discount =
                  additional_discount *
                    ((item.post_discount_amount || 0) / total_pre_vat_amount) ||
                  0;
                if (item.vat_percentage === "7") {
                  return (
                    acc +
                    ((((item.post_discount_amount || 0) -
                      item_additional_discount) /
                      1.07) *
                      parseFloat(item?.withholding_tax_type || "0")) /
                      100
                  );
                }
                return (
                  acc +
                  (((item.post_discount_amount || 0) -
                    item_additional_discount) *
                    parseFloat(item?.withholding_tax_type || "0")) /
                    100
                );
              }, 0) || 0;
        }

        net_amount =
          vat_exempted_amount +
          vat_0_percent_amount +
          vat_7_percent_amount +
          vat_amount;

        // shipping_cost = shippingCost;

        total_amount =
          net_amount -
          // - shippingCost
          withholding_tax_amount;

        return {
          additional_discount,
          // shipping_cost,
          net_amount,
          pre_vat_amount,
          pre_discount_amount,
          post_discount_amount,
          total_amount,
          vat_0_percent_amount,
          vat_7_percent_amount,
          vat_amount,
          vat_exempted_amount,
          withholding_tax_amount,
        };
      }
    );
  }, [
    // shippingCost,
    additionalDiscount,
    additionalDiscountType,
    itemList,
    priceVatType,
  ]);

  useEffect(() => {
    setValue("pre_vat_amount", summary.pre_vat_amount);
    setValue("pre_discount_amount", summary.pre_discount_amount);
    setValue("post_discount_amount", summary.post_discount_amount);
    setValue("vat_exempted_amount", summary.vat_exempted_amount);
    setValue("vat_0_percent_amount", summary.vat_0_percent_amount);
    setValue("vat_7_percent_amount", summary.vat_7_percent_amount);
    setValue("vat_amount", summary.vat_amount);
    setValue("net_amount", summary.net_amount);
    setValue("withholding_tax_amount", summary.withholding_tax_amount);
    setValue("total_amount", summary.total_amount);
  }, [
    setValue,
    summary.net_amount,
    summary.pre_discount_amount,
    summary.pre_vat_amount,
    summary.post_discount_amount,
    summary.total_amount,
    summary.vat_0_percent_amount,
    summary.vat_7_percent_amount,
    summary.vat_amount,
    summary.vat_exempted_amount,
    summary.withholding_tax_amount,
  ]);
  return summary;
};
